import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { FilterService } from '../../../../../_shared/services';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { ExecutiveStatsRequest, ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { LegendItem, MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries } from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { differenceInDays } from 'date-fns';
import { IdleTimeModalComponent } from '../../modals/idle-time-modal/idle-time-modal.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-idle-time-widget',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, GraphTrendIconComponent, EmptyStateComponent, GraphErrorStateComponent, TranslocoDirective],
  templateUrl: './idle-time-widget.component.html',
  styleUrl: './idle-time-widget.component.scss'
})
export class IdleTimeWidgetComponent extends BaseWidgetComponent {
  private modalService = inject(NgbModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  public hasError: boolean = false;
  public errorMessage: string = '';
  public hasResult: boolean = false;
  public chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 175;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  readonly legendItems: LegendItem[] = [
    { legend: this.transloco.translate('executiveStats.chart.idleTimePerDay'), color: '#EE2737' },
    { legend: this.transloco.translate('executiveStats.chart.globalTrends'), color: '#3873C9' }
  ];
  readonly series: MultiLineChartSeries[] = [
    { key: 'IdleTimePerDay', color: '#EE2737', label: this.transloco.translate('executiveStats.chart.idleTimePerDay'), showDots: false },
    { key: 'Globaltrends', color: '#3873C9', label: this.transloco.translate('executiveStats.chart.globalTrends'), showDots: false }
  ];

  public tooltipFormatter = (value: any): string => {
    return this.formatIdleTime(Number(value));
  };

  public yAxisFormatter = (value: any): string => {
    return this.formatYAxisValue(Number(value));
  };

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      const summaryRequest: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const request: ExecutiveStatsRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval
      };

      this.cancelSubject.next();
      const [idleTime, idleTimeInsights, globalIdleTime] = await Promise.all([
        this.executiveStatsService.getIdleTime(request, this.cancelSubject),
        this.executiveStatsService.getIdleTimeInsightsSummary(summaryRequest, this.cancelSubject),
        this.executiveStatsService.getGlobalIdleTime(request, this.cancelSubject)
      ]);

      this.chartData = this.processData(
        idleTime.data || [],
        globalIdleTime.data || []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      this.value = idleTimeInsights.data?.totalValue ?? 0;
      this.percentageDifference = idleTimeInsights.data?.percentageDifference ?? 0;
      this.hasResult = this.chartData.some(point =>
        this.series.some(s => (Number(point[s.key]) || 0) > 0)
      );

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  private processData(idleData: any[], globalData: any[]): MultiLineChartDataPoint[] {
    const datasets = [
      { data: idleData.map(x => ({ totalValue: Number(x.totalValue), timeSeries: new Date(x.timeSeries) })), label: 'IdleTimePerDay' },
      { data: globalData.map(x => ({ totalValue: Number(x.totalValue), timeSeries: new Date(x.timeSeries) })), label: 'Globaltrends' }
    ];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

    if (daysDifference === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets);
    } else if (daysDifference <= 7) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    } else if (daysDifference > 60) {
      return TimeSeriesGroupingHelper.groupByMonths(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    } else {
      return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private calculateYTicks(chartData: MultiLineChartDataPoint[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues: number[] = [];
    chartData.forEach(point => {
      this.series.forEach(s => {
        const value = Number(point[s.key]) || 0;
        if (value > 0) allValues.push(value);
      });
    });

    if (!allValues.length) return this.defaultYTicks;

    const max = Math.max(...allValues);
    const roundedMax = Math.max(
      Math.ceil(max / 10) * 10,
      Math.ceil(max * 1.1 / 10) * 10
    );

    const tickInterval = roundedMax / 5;
    const ticks: number[] = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(Math.round(tickInterval * i));
    }

    return ticks.sort((a, b) => b - a);
  }

  public override formatValue = (value: number): string => {
    const { hours, minutes, seconds } = this.secondsToHMS(value);
    const parts = [];
    if (hours > 0) parts.push(`${hours.toLocaleString()} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes.toLocaleString()} min${minutes > 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds.toLocaleString()} sec${seconds > 1 ? 's' : ''}`);
    return parts.join(' ') || '0 secs';
  }

  public formatDifference(value: number): string {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageDifference > 0 ? '+' : '';
    return `${sign}${this.percentageDifference.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatTotalValue(): string {
    return this.formatValue(this.value);
  }

  isUptrendPositive(): boolean {
    return false;
  }

  hasData(): boolean {
    return this.hasResult && this.chartData.length > 0;
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(IdleTimeModalComponent, modalOptions);
  }

  private formatIdleTime(value: number): string {
    const { hours, minutes, seconds } = this.secondsToHMS(value);
    const parts = [];
    if (hours > 0) parts.push(`${hours.toLocaleString()} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes.toLocaleString()} min${minutes > 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds.toLocaleString()} sec${seconds > 1 ? 's' : ''}`);
    return parts.join(' ') || '0 secs';
  }

  private formatYAxisValue(value: number): string {
    const allValues: number[] = [];
    this.chartData.forEach(point => {
      this.series.forEach(s => {
        const v = Number(point[s.key]) || 0;
        if (v > 0) allValues.push(v);
      });
    });
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

    if (maxValue < 60) {
      const secs = Math.round(value);
      return `${secs} Sec${secs !== 1 ? 's' : ''}`;
    }
    if (maxValue < 3600) {
      const mins = Math.round(value / 60);
      return `${mins} Min${mins !== 1 ? 's' : ''}`;
    }
    const hrs = Math.round(value / 3600);
    return `${hrs} Hr${hrs !== 1 ? 's' : ''}`;
  }

  private secondsToHMS(seconds: number): { hours: number, minutes: number, seconds: number } {
    const total = Math.max(0, Math.round(Number(seconds || 0)));
    return {
      hours: Math.floor(total / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    };
  }
}